<template>
  <v-container style="padding-bottom:0px">
    <v-expansion-panels class="mt-4">
      <v-expansion-panel id="indemnitor-panel">
        <v-expansion-panel-title>Indemnitor Packets</v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="indemnitor-container">

            <!-- Controls Row -->
            <div class="controls-row">
              <v-btn class="send-button" @click="openSendDialog">
                + Send Packet
              </v-btn>

              <v-select
                v-model="agentFilter"
                :items="agentOptions"
                label="Show cases for"
                density="compact"
                hide-details
                class="agent-field"
              ></v-select>

              <v-checkbox
                v-model="showArchived"
                label="Show archived"
                density="compact"
                hide-details
                class="archived-checkbox"
              ></v-checkbox>

              <div class="controls-spacer"></div>

              <v-text-field
                v-model="searchQuery"
                placeholder="name or phone"
                label="Search"
                density="compact"
                hide-details
                clearable
                class="search-field"
              ></v-text-field>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
              <p class="mt-2 loading-text">Loading indemnitor packets…</p>
            </div>

            <!-- Empty States -->
            <div v-else-if="packets.length === 0 && !hasActiveFilters" class="no-packets">
              <p>No indemnitor packets yet.</p>
              <p class="hint">Click <strong>+ Send Packet</strong> above to create one.</p>
            </div>

            <div v-else-if="packets.length === 0" class="no-packets">
              <p>No packets match your filters.</p>
            </div>

            <!-- =====================================================
                 Inner accordion: one panel per packet.
                 Single-open: opening one closes any other.
                 ===================================================== -->
            <v-expansion-panels
              v-else
              v-model="expandedPacketId"
              variant="accordion"
              class="packet-accordion"
            >
              <v-expansion-panel
                v-for="(packet, index) in packets"
                :key="packet._id"
                :value="packet._id"
                :class="['packet-panel', index % 2 === 0 ? 'row-light' : 'row-dark']"
              >
                <!-- ===== Row header (always visible) ===== -->
                <v-expansion-panel-title class="packet-panel-title">
                  <div class="row-content"
                    :class="{ 'row-archived': packet.archived }"
                    :style="{ backgroundColor: index % 2 === 0 ? '#fdfcf7' : '#fefefc' }"
                  >
                    <!-- Single-line row, laid out as 3 grid cells:
                         left (flexible) | dropdown (fixed) | right (fixed) -->
                    <div class="row-line-1">
                      <div class="row-cell-left">
                        <span class="status-badge" :class="'status-' + packet.status">
                          {{ statusLabel(packet.status) }}
                        </span>

                        <span
                          v-if="packet.language && !packet.language.startsWith('en')"
                          class="lang-badge"
                          :class="{ 'lang-badge--ru': packet.language.startsWith('ru') }"
                          :title="packet.language.startsWith('ru')
                            ? 'Sent in Russian (Русский)'
                            : 'Sent in Spanish (Español)'"
                        >{{ packet.language.startsWith('ru') ? 'RU' : 'ES' }}</span>

                        <span v-if="packet.archived" class="archived-marker">archived</span>

                        <span class="row-phone">{{ formatPhone(packet.verified_phone) }}</span>

                        <span class="row-names">
                          <span class="row-name-label">Indemnitor:</span>
                          <span class="row-name-value">{{ indemnitorFullName(packet) }}</span>
                          <span class="row-name-separator">/</span>
                          <span class="row-name-label">Defendant:</span>
                          <span class="row-name-value">{{ defendantFullName(packet) }}</span>
                        </span>

                        <!-- Glance-level progress: "X/Y" + thin bar.
                             Hidden when the schema/data isn't ready or
                             the packet has no indemnitor-facing sections
                             (would render as 0/0). -->
                        <span
                          v-if="packet.sections_total > 0"
                          class="row-progress"
                          :title="`${packet.sections_complete} of ${packet.sections_total} sections complete`"
                        >
                          <span class="row-progress-text">
                            {{ packet.sections_complete }}/{{ packet.sections_total }}
                          </span>
                          <span class="row-progress-bar">
                            <span
                              class="row-progress-bar-fill"
                              :class="{ 'row-progress-bar-fill-done':
                                packet.sections_complete === packet.sections_total }"
                              :style="{
                                width: ((packet.sections_complete / packet.sections_total) * 100) + '%'
                              }"
                            ></span>
                          </span>
                        </span>
                      </div>

                      <v-select
                        :model-value="packet.assigned_agent"
                        @update:model-value="(val) => onAgentChange(packet, val)"
                        @click.stop
                        :items="userOptions"
                        placeholder="Unassigned"
                        density="compact"
                        hide-details
                        clearable
                        class="row-agent-select"
                      ></v-select>

                      <div class="row-cell-right">
                        <span
                          v-if="packet.unacked_inbound_count > 0"
                          class="unacked-badge"
                          :title="packet.unacked_inbound_count + ' unacknowledged inbound message(s)'"
                        >
                          {{ packet.unacked_inbound_count }}
                        </span>

                        <span
                          v-if="packet.delivery_failed"
                          class="delivery-failed-badge"
                          title="Most recent SMS delivery failed"
                        >
                          !
                        </span>

                        <v-icon
                          v-if="packet.pdf_emailed_at"
                          class="pdf-emailed-icon"
                          size="16"
                          :title="pdfEmailedTooltip(packet)"
                        >mdi-email-check</v-icon>

                        <span class="row-timestamp">{{ formatTimestamp(packet.last_activity_at) }}</span>

                        <!-- Kebab menu -->
                        <v-menu location="bottom end">
                          <template v-slot:activator="{ props }">
                            <v-icon
                              v-bind="props"
                              @click.stop
                              class="kebab-icon"
                              size="20"
                            >mdi-dots-vertical</v-icon>
                          </template>
                          <v-list density="compact" class="kebab-menu-list">
                            <v-list-item @click="onViewPdfClick(packet)">
                              <v-list-item-title>View PDF</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="onArchiveClick(packet)">
                              <v-list-item-title>
                                {{ packet.archived ? 'Unarchive' : 'Archive' }}
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="onResendLinkClick(packet)">
                              <v-list-item-title>Resend Link</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="onEmailPdfClick(packet)">
                              <v-list-item-title>Email PDF</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </div>
                </v-expansion-panel-title>

                <!-- ===== Detail (only rendered for the currently-expanded panel) ===== -->
                <v-expansion-panel-text class="packet-panel-text">
                  <template v-if="expandedPacketId === packet._id">
                    <!-- Loading state while detail is fetching -->
                    <div v-if="!detail" class="loading-detail">
                      <v-progress-circular indeterminate size="20" />
                      <span class="ml-2 loading-text">Loading detail…</span>
                    </div>

                    <!-- Detail content -->
                    <div v-else class="detail-content">

                      <!-- Live progress panel — schema-driven section
                           breakdown of what the indemnitor has filled,
                           skipped, or hasn't reached yet. Updates via
                           the SSE-driven detail refetch. Guarded on
                           schema presence; if schema fetch failed, we
                           silently fall back to just showing the SMS
                           thread. -->
                      <IndemnitorProgress
                        v-if="schema && detail.packet"
                        :schema="schema"
                        :packet="detail.packet"
                      />

                      <!-- SMS thread -->
                      <div class="thread-section">
                        <div v-if="detail.thread.length === 0" class="no-messages">
                          No messages yet.
                        </div>

                        <div v-else class="thread-list" ref="threadList">
                          <div
                            v-for="msg in detail.thread"
                            :key="msg.messageSid || (msg.from + '-' + msg.receivedTimestamp)"
                            class="message-row"
                          >
                            <div class="message-row-header">
                              <span
                                class="direction-badge"
                                :class="msg.direction === 'inbound' ? 'inbound' : 'outbound'"
                              >
                                {{ msg.direction === 'inbound' ? 'Inbound' : 'Outbound' }}
                              </span>
                              <span class="message-timestamp">
                                {{ formatTimestamp(msg.receivedTimestamp) }}
                              </span>
                              <span v-if="msg.direction === 'outbound' && msg.sent_by"
                                    class="message-attribution">
                                sent by {{ msg.sent_by }}
                              </span>
                            </div>
                            <div class="message-row-body">{{ msg.body }}</div>

                            <div
                              v-if="msg.mediaUrls && msg.mediaUrls.length > 0"
                              class="message-thumbnails"
                            >
                              <a
                                v-for="(media, mIndex) in msg.mediaUrls"
                                :key="mIndex"
                                :href="proxyMediaUrl(media.url)"
                                target="_blank"
                                rel="noopener"
                                class="thumbnail-link"
                              >
                                <img
                                  :src="proxyMediaUrl(media.url)"
                                  :alt="'Attachment ' + (mIndex + 1)"
                                  class="thumbnail-img"
                                />
                              </a>
                            </div>
                          </div>
                        </div>

                        <!-- Compose box — send a manual SMS to the indemnitor -->
                        <div class="compose-box">
                          <v-text-field
                            v-model="composeText"
                            placeholder="Type a message…"
                            density="compact"
                            hide-details
                            variant="outlined"
                            class="compose-input"
                            :disabled="sendingSms"
                            @keyup.enter="onSendSmsClick"
                          ></v-text-field>
                          <v-btn class="send-sms-button"
                                 @click="onSendSmsClick"
                                 :loading="sendingSms"
                                 :disabled="!canSendSms || sendingSms">
                            Send
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </template>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>

          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- =====================================================================
         Send Packet — create a new indemnitor packet
         ===================================================================== -->
    <v-dialog v-model="sendDialogOpen" max-width="500" persistent>
      <v-card>
        <v-card-title class="dialog-title">Send Indemnitor Packet</v-card-title>
        <v-card-text class="dialog-body">
          <v-alert
            v-if="sendError"
            type="error"
            density="compact"
            class="mb-3"
            closable
            @click:close="sendError = null"
          >{{ sendError }}</v-alert>

          <v-text-field
            :model-value="sendPhone"
            @update:model-value="onSendPhoneInput"
            label="Phone number"
            placeholder="(206) 380-8493"
            density="compact"
            variant="outlined"
            :disabled="sendSubmitting"
            class="send-field"
          ></v-text-field>

          <v-select
            v-model="sendAgent"
            :items="userOptions"
            label="Assign to"
            placeholder="Unassigned"
            density="compact"
            variant="outlined"
            clearable
            :disabled="sendSubmitting"
            class="send-field"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-for="opt in languageOptions"
            :key="opt.code"
            :variant="sendLanguage === opt.code ? 'flat' : 'elevated'"
            :color="sendLanguage === opt.code ? 'primary' : undefined"
            @click="sendLanguage = opt.code"
            :disabled="sendSubmitting"
            size="small"
            class="lang-option-btn"
          >{{ opt.label }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="onSendDialogCancel"
            :disabled="sendSubmitting"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="onSendDialogSubmit"
            :loading="sendSubmitting"
            :disabled="!sendPhoneValid || sendSubmitting"
          >Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =====================================================================
         View PDF — inline modal
         ===================================================================== -->
    <v-dialog v-model="pdfModalOpen" max-width="900"
              @update:model-value="(v) => !v && closePdfModal()">
      <v-card class="pdf-dialog">
        <v-card-title class="pdf-dialog-title">
          <span>PDF Preview</span>
          <v-spacer></v-spacer>
          <v-btn icon size="small" variant="text" @click="closePdfModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pdf-dialog-body">
          <div v-if="pdfLoading" class="pdf-loading">
            <v-progress-circular indeterminate color="primary" size="32" />
            <p class="loading-text">Loading PDF…</p>
          </div>
          <v-alert v-else-if="pdfError" type="error" density="compact">
            {{ pdfError }}
          </v-alert>
          <iframe
            v-else-if="pdfBlobUrl"
            :src="pdfBlobUrl + '#toolbar=0&navpanes=0'"
            class="pdf-iframe"
            title="PDF Preview"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- =====================================================================
         Email PDF — warning dialog (shown when packet has been emailed before)
         ===================================================================== -->
    <v-dialog v-model="emailWarningOpen" max-width="540" persistent>
      <v-card>
        <v-card-title class="dialog-title">PDF already emailed</v-card-title>
        <v-card-text class="dialog-body">
          <p>
            This packet's PDF was previously emailed to
            <strong>{{ emailPacket?.pdf_emailed_by }}</strong> on
            <strong>{{ formatDate(emailPacket?.pdf_emailed_at) }}</strong>.
          </p>

          <p>
            Sending it again to a different person creates additional copies
            of sensitive personal information — name, date of birth, Social
            Security Number, and contact details. Each recipient could
            forward, save, or inadvertently expose this information. Once
            sent, you cannot recall the email.
          </p>

          <p>
            Are you sure you want to email this PDF to a different person?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="onEmailWarningCancel">Cancel</v-btn>
          <v-btn color="warning" variant="elevated" @click="onEmailWarningContinue">
            Email another person
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =====================================================================
         Email PDF — user-picker dialog
         ===================================================================== -->
    <v-dialog v-model="emailDialogOpen" max-width="540" persistent>
      <v-card>
        <v-card-title class="dialog-title">Email PDF</v-card-title>
        <v-card-text class="dialog-body">
          <v-alert
            v-if="emailError"
            type="error"
            density="compact"
            class="mb-3"
            closable
            @click:close="emailError = null"
          >{{ emailError }}</v-alert>

          <p class="dialog-prompt">Choose a recipient:</p>

          <v-radio-group v-model="emailRecipient" hide-details density="compact">
            <v-radio
              v-for="user in companyUsers"
              :key="user.email"
              :value="user.email"
              :label="userLabel(user)"
              :disabled="emailSending"
            ></v-radio>
          </v-radio-group>

          <p v-if="!companyUsers.length" class="empty-users-note">
            No BBAS users found for this company.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            @click="onEmailDialogCancel"
            :disabled="emailSending"
          >Cancel</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="onEmailDialogSend"
            :loading="emailSending"
            :disabled="!emailRecipient || emailSending"
          >Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =====================================================================
         Resend Link — acknowledgment dialog for submitted-packet reverts
         =====================================================================
         Shown after a successful resend that also reverted a submitted
         packet back to in_progress. Replaces the normal "Link resent"
         snackbar in that case — the state change is significant enough
         that staff should explicitly acknowledge it, not have it flash
         past in transient feedback. -->
    <v-dialog v-model="revertAckOpen" max-width="540" persistent>
      <v-card>
        <v-card-title class="dialog-title">Packet reopened</v-card-title>
        <v-card-text class="dialog-body">
          <p>
            This packet was in <strong>Submitted</strong> state. The link has
            been resent and the packet moved back to
            <strong>In Progress</strong>. The indemnitor can now revise and
            resubmit their application.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" @click="revertAckOpen = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =====================================================================
         Snackbar — transient action feedback (resend, archive errors, etc.)
         ===================================================================== -->
    <v-snackbar
      v-model="snackbarOpen"
      :color="snackbarColor"
      :timeout="3000"
      location="bottom"
    >
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbarOpen = false">Close</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';

import IndemnitorProgress from './CompanyAccount-Indemnitor-Progress.vue';

// Closed locale set for the Send Packet dialog. The backend accepts the
// same three codes for every customer; locale support is no longer
// gated per-customer. Each label is the locale's name in its own
// script — convention used by the indemnitor SPA's locale toggle.
const LANGUAGE_OPTIONS = [
  { code: 'en-US', label: 'English' },
  { code: 'es-MX', label: 'Español' },
  { code: 'ru-RU', label: 'Русский' },
];

export default {
  name: 'CompanyAccountIndemnitor',

  components: {
    IndemnitorProgress,
  },

  data() {
    return {
      // ============================ List state ============================
      loading: false,
      packets: [],
      companyUsers: [],         // From /portaldb/db/users/<companyNumber>
      searchQuery: '',
      agentFilter: 'all',
      showArchived: false,
      searchDebounceTimer: null,

      // ============================ Schema (per-customer) =================
      // Loaded once on mount via /staff/schema; immutable at runtime.
      // Drives the live progress panel inside each expanded packet
      // (sections, field labels, sensitivity flags, conditions).
      // Stays null while loading and on schema-fetch failure — child
      // components must guard with `v-if="schema"`.
      schema: null,

      // ============================ Detail state ==========================
      // Vuetify v-expansion-panels v-model — the value of the open panel.
      // Each panel sets :value="packet._id", so this is "currently open
      // packet ID" or undefined when nothing is open.
      expandedPacketId: undefined,
      detail: null,            // { packet, branding, thread } for the expanded packet
      detailLoading: false,
      composeText: '',
      sendingSms: false,

      // ============================ Snackbar state =======================
      // Toast notifications for transient action feedback (e.g., "Link
      // resent", "Failed to archive"). 3s auto-dismiss.
      snackbarOpen: false,
      snackbarText: '',
      snackbarColor: 'success',

      // ============================ Send Packet dialog state =============
      // "+ Send Packet" creates a new packet via POST /staff/packets.
      // Phone is validated client-side (≥10 digits after stripping non-digits).
      // sendLanguage holds the chosen locale code (en-US default; flips to
      // es-MX or ru-RU when the agent picks one of the language buttons).
      sendDialogOpen: false,
      sendPhone: '',
      sendAgent: null,
      sendLanguage: 'en-US',
      sendSubmitting: false,
      sendError: null,

      // ============================ View PDF modal state =================
      // Inline-PDF modal — fetches the PDF as a blob via axios (which
      // attaches our Bearer token), then renders it in an <iframe> via a
      // blob: URL. The blob URL is revoked on close to free memory.
      pdfModalOpen: false,
      pdfPacket: null,
      pdfBlobUrl: null,
      pdfLoading: false,
      pdfError: null,

      // ============================ Email PDF dialog state ===============
      // Two-dialog flow: if pdf_emailed_at is set on the packet we show a
      // warning first, then transition to the user-picker on confirm.
      // Otherwise straight to user-picker.
      emailWarningOpen: false,
      emailDialogOpen: false,
      emailPacket: null,
      emailRecipient: null,
      emailSending: false,
      emailError: null,

      // ============================ Resend Link revert ack ===============
      // Shown after a successful resend that also reverted a submitted
      // packet back to in_progress. Replaces the normal "Link resent"
      // snackbar in that case — the state change deserves an explicit
      // acknowledgment, not transient feedback.
      revertAckOpen: false,

      // ============================ SSE state =============================
      eventSource: null,
      sseListRefetchTimer: null,
      sseDetailRefetchTimer: null,
    };
  },

  computed: {
    ...mapGetters(['getSelectedCompany']),

    companyNumber() {
      return this.getSelectedCompany?.companyNumber;
    },

    timezone() {
      return this.getSelectedCompany?.timezone || 'America/Los_Angeles';
    },

    // Top-of-list filter dropdown options. "All agents" + "Unassigned"
    // pseudo-values, then one entry per BBAS user from
    // /portaldb/db/users/<companyNumber>. Same source as the per-row
    // assignment v-select (userOptions) so the filter and the
    // assignment picker stay in lockstep — anyone you can assign,
    // you can filter by.
    //
    // Caveat: legacy packets assigned to a name no longer in
    // bbas.users won't have a filter chip. The free-text search box
    // covers those if needed.
    agentOptions() {
      return [
        { title: 'All agents', value: 'all' },
        { title: 'Unassigned', value: 'unassigned' },
        ...this.userOptions.map(name => ({ title: name, value: name })),
      ];
    },

    // List of names for the per-row assignment v-select. Sourced strictly
    // from company.users (BBAS users for the current company). Legacy
    // assignments to users no longer in the list won't appear in the
    // dropdown — they're stuck on whatever name the packet has until
    // someone reassigns.
    userOptions() {
      return this.companyUsers
        .map(u => {
          const composedName = `${u.firstName || ''} ${u.lastName || ''}`.trim();
          return u.name || composedName || u.email || null;
        })
        .filter(Boolean);
    },

    // Surfaces the module-level LANGUAGE_OPTIONS array to the template.
    // Static — never changes during the component lifetime.
    languageOptions() {
      return LANGUAGE_OPTIONS;
    },

    hasActiveFilters() {
      return (this.searchQuery && this.searchQuery.trim() !== '') ||
             this.agentFilter !== 'all' ||
             this.showArchived === true;
    },

    // Send-Packet phone validation. Strips non-digits and requires at least
    // 10 (US-style). Backend canonicalizes to E.164.
    sendPhoneValid() {
      const digits = (this.sendPhone || '').replace(/\D/g, '');
      return digits.length >= 10;
    },

    // Compose-box validation — non-empty after trim. Backend enforces this
    // too but disabling the Send button avoids a wasted round-trip.
    canSendSms() {
      return (this.composeText || '').trim().length > 0;
    },
  },

  watch: {
    searchQuery() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchPackets();
      }, 300);
    },
    agentFilter() { this.fetchPackets(); },
    showArchived() { this.fetchPackets(); },

    // Vuetify sets expandedPacketId to the open panel's :value, or
    // undefined when collapsed. Watch fires fetch on open, clears on close.
    expandedPacketId(newVal) {
      if (newVal) {
        this.fetchDetail(newVal);
      } else {
        this.detail = null;
        this.composeText = '';
      }
    },
  },

  methods: {
    // -------------------------------------------------------------
    // List data loading
    // -------------------------------------------------------------

    async loadData() {
      if (!this.companyNumber) return;
      this.loading = true;
      try {
        const [packetsResp, usersResp, schemaResp] = await Promise.all([
          this._fetchPacketsRequest(),
          axios.get(`/portaldb/db/users/${this.companyNumber}`),
          axios.get(`/indemnitor/entry/staff/schema?companyNumber=${this.companyNumber}`),
        ]);
        if (packetsResp?.data?.success) this.packets = packetsResp.data.data || [];
        // portalAPI returns the array directly (no { success, data } wrapper).
        if (Array.isArray(usersResp?.data)) this.companyUsers = usersResp.data;
        // Schema failure is non-fatal — list still works without it,
        // expanded panel falls back to the SMS thread alone.
        if (schemaResp?.data?.success) this.schema = schemaResp.data.data || null;
      } catch (err) {
        console.error('[Indemnitor] loadData error:', err);
      } finally {
        this.loading = false;
      }
    },

    async fetchPackets() {
      if (!this.companyNumber) return;
      try {
        const resp = await this._fetchPacketsRequest();
        if (resp?.data?.success) this.packets = resp.data.data || [];
      } catch (err) {
        console.error('[Indemnitor] fetchPackets error:', err);
      }
    },

    _fetchPacketsRequest() {
      const params = new URLSearchParams({
        companyNumber: String(this.companyNumber),
      });
      const q = (this.searchQuery || '').trim();
      if (q) params.set('search', q);
      if (this.agentFilter === 'unassigned') {
        params.set('unassigned', 'true');
      } else if (this.agentFilter !== 'all') {
        params.set('assignedAgent', this.agentFilter);
      }
      if (this.showArchived) params.set('archived', 'true');
      return axios.get(`/indemnitor/entry/staff/packets?${params.toString()}`);
    },

    // -------------------------------------------------------------
    // Detail data loading + acknowledge-inbound
    // -------------------------------------------------------------
    //
    // fetchDetail is called when a panel expands (watch on
    // expandedPacketId), and any time SSE indicates the open packet's
    // data has changed.
    //
    // Stale-fetch guard: rapid clicking between rows could land a slow
    // fetch's result on a packet the user already navigated away from.
    // Before assigning to this.detail, confirm expandedPacketId still
    // matches the packet this fetch was for.
    //
    // After every successful detail fetch, _ackInbound() chains. The
    // ack endpoint is idempotent; the chain covers both "open detail"
    // and "new inbound while open" with one path.

    async fetchDetail(packetId) {
      if (!packetId || !this.companyNumber) return;
      this.detailLoading = true;
      try {
        const resp = await axios.get(
          `/indemnitor/entry/staff/packets/${packetId}` +
          `?companyNumber=${this.companyNumber}`
        );
        if (resp?.data?.success && this.expandedPacketId === packetId) {
          this.detail = resp.data.data;
          this._ackInbound();
          this._scrollThreadToBottom();
        }
      } catch (err) {
        console.error('[Indemnitor] fetchDetail error:', err);
      } finally {
        if (this.expandedPacketId === packetId) {
          this.detailLoading = false;
        }
      }
    },

    _scrollThreadToBottom() {
      // Three scrolls staggered over time, each catching a different
      // layout-shift cause:
      //   nextTick — covers a text-only thread; scrolls as soon as
      //              Vue has painted the thread DOM.
      //   400ms   — gives Vuetify's panel-expand animation (~300ms)
      //              time to settle before scrolling again.
      //   1500ms  — catches MMS thumbnails finishing their network
      //              load. <img> elements grow the thread's
      //              scrollHeight asynchronously as they decode, and
      //              there's no Vue lifecycle hook for "all images
      //              done." A "big old timeout" is the cheapest way
      //              to land at the bottom in the common case
      //              without wiring up per-image load handlers or a
      //              ResizeObserver.
      // Cost of an unnecessary scroll is nothing — scrollTop is just
      // re-set to its current value if nothing's grown.
      //
      // Note on the array unwrap: the ref="threadList" element lives
      // inside the v-for over packets, so Vue 3 hands us an array
      // even though the v-if gate means at most one element actually
      // carries the ref at any moment. Without unwrapping, every
      // assignment below is a silent no-op on the array — which is
      // why this scroll-to-bottom appeared to "never quite work."
      const scroll = () => {
        let el = this.$refs.threadList;
        if (Array.isArray(el)) el = el[0];
        if (el) el.scrollTop = el.scrollHeight;
      };
      this.$nextTick(scroll);
      setTimeout(scroll, 400);
      setTimeout(scroll, 1500);
    },

    async _ackInbound() {
      if (!this.expandedPacketId || !this.companyNumber) return;
      const packetId = this.expandedPacketId;
      try {
        const resp = await axios.post(
          `/indemnitor/entry/staff/packets/${packetId}/acknowledge-inbound`,
          { companyNumber: this.companyNumber }
        );
        const count = resp?.data?.data?.count;
        if (count > 0) {
          console.log(`[Indemnitor] acked ${count} inbound message(s)`);
        }
      } catch (err) {
        console.error('[Indemnitor] acknowledgeInbound error:', err);
      }
    },

    // -------------------------------------------------------------
    // Per-row agent assignment
    // -------------------------------------------------------------
    //
    // Each row's v-select is bound :model-value="packet.assigned_agent"
    // with @update:model-value firing onAgentChange below. We optimistically
    // mutate the local packet so the dropdown shows the new value
    // immediately, then PATCH. On failure we revert. SSE refetch will
    // reconcile if needed.

    async onAgentChange(packet, newAgent) {
      const cleaned = (newAgent && typeof newAgent === 'string') ? newAgent.trim() : null;
      const finalVal = cleaned || null;            // empty / null → unassign
      const previous = packet.assigned_agent || null;
      if (previous === finalVal) return;           // no-op

      // Optimistic update — the dropdown re-renders immediately.
      packet.assigned_agent = finalVal;

      try {
        await axios.patch(
          `/indemnitor/entry/staff/packets/${packet._id}`,
          {
            companyNumber: this.companyNumber,
            assignedAgent: finalVal,
          }
        );
      } catch (err) {
        console.error('[Indemnitor] onAgentChange error:', err);
        // Revert on failure.
        packet.assigned_agent = previous;
      }
    },

    // -------------------------------------------------------------
    // Kebab menu actions — stubs for Steps 16/17
    // -------------------------------------------------------------

    // -------------------------------------------------------------
    // Archive / Unarchive (Step 17)
    // -------------------------------------------------------------
    //
    // Toggle on packet.archived via PATCH. Optimistic — the local packet
    // is mutated immediately so the row's opacity fades/unfades without
    // waiting for the server. SSE refetch arrives in ~500ms and reconciles.
    // On failure: revert and show snackbar.
    //
    // The kebab item label switches between "Archive" and "Unarchive" via
    // template binding on packet.archived, so this single handler covers
    // both directions.

    async onArchiveClick(packet) {
      const newValue = !packet.archived;
      const previous = packet.archived;
      packet.archived = newValue;
      try {
        const resp = await axios.patch(
          `/indemnitor/entry/staff/packets/${packet._id}`,
          {
            companyNumber: this.companyNumber,
            archived: newValue,
          }
        );
        if (!resp?.data?.success) {
          packet.archived = previous;
          this.showSnackbar(
            resp?.data?.message || 'Failed to update archive status',
            'error'
          );
        }
        // On success: no snackbar — the row's opacity change is the feedback.
      } catch (err) {
        console.error('[Indemnitor] archive error:', err);
        packet.archived = previous;
        this.showSnackbar('Failed to update archive status', 'error');
      }
    },

    // -------------------------------------------------------------
    // Resend Link (Step 17)
    // -------------------------------------------------------------
    //
    // Re-fires the magic-link SMS in the packet's stored language with a
    // fresh expiry. Backend always returns 200 on success even if Twilio
    // rejected the message — we have to look at smsResult.sent for the
    // real outcome (matches createPacket / sendStaffSms / emailPdf
    // pattern).
    //
    // If the packet was in submitted state, the backend reverts it to
    // in_progress as part of the resend and signals this via
    // data.reverted. In that case we show an acknowledgment dialog
    // instead of the usual snackbar — the state revert is significant
    // enough that staff should see an explicit "OK" to dismiss, and the
    // dialog itself communicates resend success, so we don't also fire
    // the snackbar. SSE picks up the status change and the row updates
    // automatically.

    async onResendLinkClick(packet) {
      try {
        const resp = await axios.post(
          `/indemnitor/entry/staff/packets/${packet._id}/resend-link`,
          { companyNumber: this.companyNumber }
        );
        if (resp?.data?.success) {
          const data = resp?.data?.data || {};
          const sent = data.smsResult?.sent;
          if (sent) {
            if (data.reverted) {
              this.revertAckOpen = true;
            } else {
              this.showSnackbar('Link resent', 'success');
            }
          } else {
            const errMsg = data.smsResult?.error || 'SMS delivery failed';
            this.showSnackbar(`Resend failed: ${errMsg}`, 'error');
          }
        } else {
          this.showSnackbar(
            resp?.data?.message || 'Resend failed',
            'error'
          );
        }
      } catch (err) {
        console.error('[Indemnitor] resend error:', err);
        const msg = err?.response?.data?.message || 'Resend failed';
        this.showSnackbar(msg, 'error');
      }
    },

    // -------------------------------------------------------------
    // Compose box — send a manual SMS to the indemnitor (Step 18)
    // -------------------------------------------------------------
    //
    // POST to /staff/packets/:id/sms with { companyNumber, body }.
    // Same pattern as resendLink: backend always returns 200 on success
    // even if Twilio rejected; we look at smsResult.sent to know the
    // real outcome.
    //
    // On send success: clear the compose input. The new message appears
    // in the thread automatically — the SSE smsMessages event triggers
    // a detail refetch which redraws the thread and scrolls to bottom.
    // No success snackbar; the new bubble appearing IS the feedback.

    async onSendSmsClick() {
      const body = (this.composeText || '').trim();
      if (!body || !this.expandedPacketId) return;
      const packetId = this.expandedPacketId;

      this.sendingSms = true;
      try {
        const resp = await axios.post(
          `/indemnitor/entry/staff/packets/${packetId}/sms`,
          {
            companyNumber: this.companyNumber,
            body,
          }
        );
        if (resp?.data?.success) {
          const sent = resp?.data?.data?.smsResult?.sent;
          if (sent) {
            this.composeText = '';
          } else {
            const errMsg = resp?.data?.data?.smsResult?.error
              || 'SMS delivery failed';
            this.showSnackbar(`Send failed: ${errMsg}`, 'error');
          }
        } else {
          this.showSnackbar(
            resp?.data?.message || 'Failed to send message',
            'error'
          );
        }
      } catch (err) {
        console.error('[Indemnitor] sendStaffSms error:', err);
        const msg = err?.response?.data?.message || 'Failed to send message';
        this.showSnackbar(msg, 'error');
      } finally {
        this.sendingSms = false;
      }
    },

    // -------------------------------------------------------------
    // Snackbar helper — small wrapper around v-snackbar state
    // -------------------------------------------------------------

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbarOpen = true;
    },

    // -------------------------------------------------------------
    // Send Packet flow (Step 15)
    // -------------------------------------------------------------
    //
    // Dialog with phone + agent + language picker (en-US / es-MX / ru-RU).
    // POST creates the packet and fires the link SMS in the chosen
    // language. List refreshes via SSE; new packet appears at the top.

    openSendDialog() {
      this._resetSendState();
      this.sendDialogOpen = true;
    },

    // Format-as-you-type for the Send Packet phone field. Pattern lifted
    // from FieldPhone.vue in the indemnitor SPA, kept simple: strip to
    // digits, drop a leading 1 if 11 long, format up to 10 as US display.
    // International numbers (anything starting with "+" other than "+1")
    // pass through unchanged so the agent can enter them raw — the
    // backend's _normalizePhoneE164 handles whatever ends up in there.
    // Cursor goes to end on each reformat; matches FieldPhone behavior.
    onSendPhoneInput(raw) {
      this.sendPhone = this._formatSendPhone(raw || '');
    },

    _formatSendPhone(raw) {
      if (!raw) return '';

      // Pure international (not +1): leave alone. Agent presumably knows
      // what they're typing.
      if (raw.startsWith('+') && !raw.startsWith('+1')) return raw;

      // Otherwise: strip to digits, drop leading 1 if 11 digits, format.
      // Handles "+12065551234", "12065551234", "(206) 555-1234", etc.
      let digits = raw.replace(/\D/g, '');
      if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
      digits = digits.slice(0, 10);

      if (digits.length === 0) return '';
      if (digits.length <= 3) return `(${digits}`;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    },

    onSendDialogCancel() {
      this.sendDialogOpen = false;
      this._resetSendState();
    },

    async onSendDialogSubmit() {
      if (!this.sendPhoneValid) return;
      this.sendSubmitting = true;
      this.sendError = null;
      try {
        // Strip non-digits before sending; backend canonicalizes to E.164.
        const digits = this.sendPhone.replace(/\D/g, '');
        const resp = await axios.post('/indemnitor/entry/staff/packets', {
          companyNumber: this.companyNumber,
          phone: digits,
          assignedAgent: this.sendAgent || null,
          language: this.sendLanguage,
        });
        if (resp?.data?.success) {
          this.sendDialogOpen = false;
          this._resetSendState();
        } else {
          this.sendError = resp?.data?.message || 'Failed to send packet.';
        }
      } catch (err) {
        console.error('[Indemnitor] sendPacket error:', err);
        this.sendError = err?.response?.data?.message
          || 'Failed to send packet.';
      } finally {
        this.sendSubmitting = false;
      }
    },

    _resetSendState() {
      this.sendPhone = '';
      this.sendAgent = null;
      this.sendLanguage = 'en-US';
      this.sendError = null;
    },

    // -------------------------------------------------------------
    // View PDF (inline modal)
    // -------------------------------------------------------------
    //
    // GETs /staff/packets/:id/pdf-view as a binary blob (axios responseType
    // 'blob'), wraps it in a blob: URL, and renders inside an iframe in a
    // dialog. Browsers handle PDF rendering natively. Auth flows through
    // our standard axios interceptor (Bearer token); no token in URLs.
    //
    // The blob URL is revoked when the modal closes to free memory. We
    // also revoke any prior blob URL when opening a new one, so rapid
    // open/close/open of different packets doesn't leak.

    onViewPdfClick(packet) {
      this.openPdfModal(packet);
    },

    async openPdfModal(packet) {
      this._revokePdfBlob();
      this.pdfPacket = packet;
      this.pdfModalOpen = true;
      this.pdfLoading = true;
      this.pdfError = null;
      try {
        const resp = await axios.get(
          `/indemnitor/entry/staff/packets/${packet._id}/pdf-view`,
          {
            params: { companyNumber: this.companyNumber },
            responseType: 'blob',
          }
        );
        if (resp?.data instanceof Blob) {
          this.pdfBlobUrl = URL.createObjectURL(resp.data);
        } else {
          this.pdfError = 'Unexpected response format.';
        }
      } catch (err) {
        console.error('[Indemnitor] PDF view error:', err);
        this.pdfError = 'Failed to load PDF. Please try again.';
      } finally {
        this.pdfLoading = false;
      }
    },

    closePdfModal() {
      this.pdfModalOpen = false;
      this._revokePdfBlob();
      this.pdfPacket = null;
      this.pdfError = null;
    },

    _revokePdfBlob() {
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
        this.pdfBlobUrl = null;
      }
    },

    // -------------------------------------------------------------
    // Email PDF flow (Step 16)
    // -------------------------------------------------------------
    //
    // Two-dialog pattern:
    //   1. If packet.pdf_emailed_at is set → show warning dialog first.
    //      User can Cancel (closes everything) or "Email another person"
    //      (transitions to user-picker).
    //   2. User-picker dialog: pick a BBAS user from the list, click Send.
    //      POST /staff/packets/:id/email-pdf with { companyNumber, recipient }.
    //
    // Backend handles PDF generation, email sending, audit logging, and
    // updating pdf_emailed_at / pdf_emailed_by. The list & detail will
    // refresh via SSE on success.

    onEmailPdfClick(packet) {
      this._resetEmailState();
      this.emailPacket = packet;
      if (packet.pdf_emailed_at) {
        this.emailWarningOpen = true;
      } else {
        this.emailDialogOpen = true;
      }
    },

    onEmailWarningCancel() {
      this.emailWarningOpen = false;
      this.emailPacket = null;
    },

    onEmailWarningContinue() {
      this.emailWarningOpen = false;
      this.emailDialogOpen = true;
    },

    onEmailDialogCancel() {
      this.emailDialogOpen = false;
      this._resetEmailState();
    },

    async onEmailDialogSend() {
      if (!this.emailPacket || !this.emailRecipient) return;
      const packetId = this.emailPacket._id;
      this.emailSending = true;
      this.emailError = null;
      try {
        const resp = await axios.post(
          `/indemnitor/entry/staff/packets/${packetId}/email-pdf`,
          {
            companyNumber: this.companyNumber,
            recipient: this.emailRecipient,
          }
        );
        if (resp?.data?.success) {
          this.emailDialogOpen = false;
          this._resetEmailState();
        } else {
          this.emailError = resp?.data?.message || 'Failed to send email. Please try again.';
        }
      } catch (err) {
        console.error('[Indemnitor] email-pdf error:', err);
        this.emailError = err?.response?.data?.message
          || 'Failed to send email. Please try again.';
      } finally {
        this.emailSending = false;
      }
    },

    _resetEmailState() {
      this.emailPacket = null;
      this.emailRecipient = null;
      this.emailError = null;
    },

    // Label for a single user in the picker. "Eric Juvet <eric@bbas.com>"
    // when name is available, otherwise just the email address.
    userLabel(user) {
      const composed = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const name = user.name || composed;
      return name ? `${name} <${user.email}>` : (user.email || '');
    },

    // -------------------------------------------------------------
    // SSE — live updates
    // -------------------------------------------------------------

    setupSSE() {
      if (!this.companyNumber) return;
      this.teardownSSE();

      const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const url = `/indemnitor/entry/staff/events/${uuid}/${this.companyNumber}`;
      console.log('[Indemnitor SSE] opening:', url);

      const es = new EventSource(url);

      es.addEventListener('packets', (event) => {
        let doc = null;
        try { doc = JSON.parse(event.data); } catch { /* ignore */ }
        this._scheduleListRefetch();
        if (doc && this.expandedPacketId &&
            String(doc._id) === String(this.expandedPacketId)) {
          this._scheduleDetailRefetch();
        }
      });

      es.addEventListener('smsMessages', (event) => {
        let doc = null;
        try { doc = JSON.parse(event.data); } catch { /* ignore */ }
        this._scheduleListRefetch();
        if (doc && this.expandedPacketId && this.detail?.packet?.verified_phone) {
          const phone = this.detail.packet.verified_phone;
          if (doc.from === phone || doc.to === phone) {
            this._scheduleDetailRefetch();
          }
        }
      });

      es.addEventListener('error', (err) => {
        console.warn('[Indemnitor SSE] error (auto-reconnect handles):', err);
      });

      this.eventSource = es;
    },

    teardownSSE() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      clearTimeout(this.sseListRefetchTimer);
      clearTimeout(this.sseDetailRefetchTimer);
      this.sseListRefetchTimer = null;
      this.sseDetailRefetchTimer = null;
    },

    _scheduleListRefetch() {
      clearTimeout(this.sseListRefetchTimer);
      this.sseListRefetchTimer = setTimeout(() => {
        this.fetchPackets();
      }, 500);
    },

    _scheduleDetailRefetch() {
      clearTimeout(this.sseDetailRefetchTimer);
      this.sseDetailRefetchTimer = setTimeout(() => {
        if (this.expandedPacketId) this.fetchDetail(this.expandedPacketId);
      }, 500);
    },

    // -------------------------------------------------------------
    // Formatting helpers
    // -------------------------------------------------------------

    statusLabel(status) {
      const labels = {
        created: 'Sent',
        in_progress: 'In progress',
        submitted: 'Submitted',
      };
      return labels[status] || status;
    },

    formatPhone(phone) {
      if (!phone) return 'N/A';
      if (phone.startsWith('+1') && phone.length === 12) {
        return `(${phone.substring(2, 5)}) ${phone.substring(5, 8)}-${phone.substring(8)}`;
      }
      return phone;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).tz(this.timezone).format('DD MMM h:mma, ddd');
    },

    formatDate(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).tz(this.timezone).format('DD MMM');
    },

    indemnitorFullName(packet) {
      const f = (packet.indemnitor?.indemnitor_first_name || '').trim();
      const l = (packet.indemnitor?.indemnitor_last_name  || '').trim();
      return `${f} ${l}`.trim() || '—';
    },

    // Build a same-origin URL that proxies a Twilio media URL through the
    // indemnitor service (Basic auth happens server-side; browser just
    // does a normal GET). Used by message-thumbnail <img> tags.
    proxyMediaUrl(url) {
      return `/indemnitor/entry/staff/media`
        + `?companyNumber=${this.companyNumber}`
        + `&url=${encodeURIComponent(url)}`;
    },

    defendantFullName(packet) {
      const f = (packet.bond?.defendant_first_name || '').trim();
      const l = (packet.bond?.defendant_last_name  || '').trim();
      return `${f} ${l}`.trim() || '—';
    },

    // Tooltip for the PDF-emailed icon. Looks up the user by email in
    // companyUsers to surface a human-readable name alongside the email.
    // Format: "PDF emailed 02 May to BBAS User Name / user@example.com"
    pdfEmailedTooltip(packet) {
      if (!packet.pdf_emailed_at) return '';
      const date = this.formatDate(packet.pdf_emailed_at);
      const email = packet.pdf_emailed_by || '';
      const user = this.companyUsers.find(u => u.email === email);
      let name = '';
      if (user) {
        const composed = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        name = user.name || composed || '';
      }
      if (name) {
        return `PDF emailed ${date} to ${name} / ${email}`;
      }
      return `PDF emailed ${date} to ${email}`;
    },
  },

  async mounted() {
    await this.loadData();
    this.setupSSE();
  },

  beforeUnmount() {
    clearTimeout(this.searchDebounceTimer);
    this.teardownSSE();
  },
};
</script>

<style scoped>
.indemnitor-container {
  padding: 10px 0;
}

/* =================================================================
   Controls row
   ================================================================= */
.controls-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.send-button {
  background-color: #d2b48c !important;
  flex-shrink: 0;
}

.agent-field { flex: 0 1 200px; }
.archived-checkbox { flex-shrink: 0; }
.controls-spacer { flex: 1; }
.search-field { flex: 0 1 280px; }

.loading-text {
  font-size: 14px;
  color: #666;
}

.no-packets {
  text-align: center;
  color: #666;
  padding: 30px 20px;
}
.no-packets p { margin: 4px 0; }
.no-packets .hint { font-size: 13px; color: #888; }

/* =================================================================
   Inner accordion — alternating very-pale-tan rows with no gap.
   The accordion container holds one outer border; each row's
   background comes from .row-light / .row-dark.
   ================================================================= */
.packet-accordion {
  background-color: transparent !important;
  border: 1px solid #e0d5c0;
  border-radius: 4px;
  overflow: hidden;
}

:deep(.packet-panel) {
  background-color: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  margin-bottom: 0 !important;
}

/* Alternating row backgrounds. Setting these on .v-expansion-panel-title
   directly because Vuetify's title element has its own background that
   was masking the panel-level bg. Two rules per variant: panel itself
   (covers any uncovered area) AND title (the actual visible header). */
:deep(.packet-panel.row-light),
:deep(.packet-panel.row-light .v-expansion-panel-title) {
  background-color: #fdfcf7 !important;
}
:deep(.packet-panel.row-dark),
:deep(.packet-panel.row-dark .v-expansion-panel-title) {
  background-color: #fefefc !important;
}

/* Override Vuetify's default panel-title styling for inner panels.
   No padding here — .row-content provides padding and bg, so its
   inline-styled background fills the full title width edge-to-edge.
   
   Higher specificity than the bare `.v-expansion-panel-title` is needed
   because the parent component (CompanyAccount.vue) sets min-height
   and padding via :deep() with selector specificity (0,4,0). Adding
   .packet-accordion + the compound .packet-panel-title.v-expansion-panel-title
   gets us to (0,5,0) so we win. */
:deep(.packet-accordion .packet-panel .packet-panel-title.v-expansion-panel-title) {
  color: #333 !important;
  border: none !important;
  min-height: 0 !important;
  padding: 0 !important;
  font-size: 14px !important;
  font-weight: 400 !important;
}
:deep(.packet-panel .v-expansion-panel-title) ,
:deep(.packet-panel .v-expansion-panel-title) * {
  font-weight: 400 !important;
}

/* Hide the expand/collapse chevron — pointer cursor + hover state are
   sufficient affordances for clickability. */
:deep(.packet-panel .v-expansion-panel-title__icon) {
  display: none !important;
}

/* Subtle hover state. Slightly darker than the row's own bg — works
   for both light and dark alternating rows because it tints relative
   to whatever's underneath via semi-transparency. */
:deep(.packet-panel .v-expansion-panel-title:hover) {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

/* Suppress Vuetify's default hover overlay so it doesn't fight ours. */
:deep(.packet-panel .v-expansion-panel-title__overlay) {
  opacity: 0 !important;
}

/* Body of expanded panel: distinct surface so the detail reads as
   separate from the row header above it. */
:deep(.packet-panel .v-expansion-panel-text) {
  background-color: #fffbf5 !important;
  border-top: 1px solid #ece4d4 !important;
}

:deep(.packet-panel .v-expansion-panel-text__wrapper) {
  padding: 12px 16px !important;
}

/* Row content layout — the slot inside v-expansion-panel-title.
   The inline :style on this div sets the alternating bg, so this div
   needs to fill the entire title (no leftover edges showing through). */
.row-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  align-self: stretch;
  padding: 8px 12px;
  /* Vuetify's title is a flex row with the chevron on the right; our
     content sits in a column to its left. */
}

/* Archived rows fade to deemphasize them in the list. We apply opacity
   to the inner grid (.row-line-1), NOT to .row-content, so the row's
   background color stays solid. Otherwise opacity on the bg-bearing
   element bleeds through to the outer accordion's beige and the row
   reads as medium tan rather than faded. The dropdown and kebab are
   still clickable; their popup content is portaled outside this opacity
   context, so menus open at full opacity regardless. */
.row-content.row-archived > .row-line-1 {
  opacity: 0.55;
}

/* Single-line row laid out as a 3-column grid:
   [left content (flexible) | agent dropdown (fixed) | right indicators (fixed)]
   Fixed widths on cells 2 and 3 ensure the agent dropdown lands at the
   same X position across every row, regardless of name length or which
   indicators are present. */
.row-line-1 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px 260px;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.row-cell-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;       /* allow names to truncate instead of pushing the grid */
  overflow: hidden;
}

.row-cell-right {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}

.row-phone {
  color: #333;
  white-space: nowrap;
  flex-shrink: 0;
}

.row-names {
  color: #555;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.row-name-label {
  color: #999;
  font-size: 12px;
}

.row-name-value {
  color: #333;
}

.row-name-separator {
  color: #ccc;
  margin: 0 4px;
}

/* Row-header glance progress indicator — "X/Y" + thin bar.
   Compact so it doesn't push the names off the row. The bar's fill
   shifts to a success color once the packet's all-done. */
.row-progress {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  font-size: 12px;
  color: #777;
  font-variant-numeric: tabular-nums;
  margin-left: 4px;
}

.row-progress-text {
  white-space: nowrap;
}

.row-progress-bar {
  display: inline-block;
  width: 56px;
  height: 4px;
  background-color: #e8e0cc;
  border-radius: 2px;
  overflow: hidden;
}

.row-progress-bar-fill {
  display: block;
  height: 100%;
  background-color: #b8a073;
  transition: width 200ms ease;
}

.row-progress-bar-fill-done {
  background-color: #4caf50;
}

.row-timestamp {
  font-size: 13px;
  color: #888;
  flex-shrink: 0;
  white-space: nowrap;
}

.unacked-badge {
  background-color: #d32f2f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  flex-shrink: 0;
}
.delivery-failed-badge {
  background-color: #ff9800;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  flex-shrink: 0;
}

.pdf-emailed-icon {
  color: #888 !important;
  flex-shrink: 0;
}

.archived-marker {
  font-size: 11px;
  color: #b87100;
  background-color: #fff4e0;
  border: 1px solid #ffd599;
  padding: 0 6px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* Language badge — small light-blue pill next to status, only shown for
   non-en-US packets. Subtle so it doesn't compete with the status pill.
   Default styling is for ES (blue); RU overrides via .lang-badge--ru. */
.lang-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 3px;
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

/* Russian variant — Material Purple, distinct from the ES blue at a
   glance. Same shape and weight; just color shift. */
.lang-badge--ru {
  background-color: #f3e5f5;
  color: #6a1b9a;
  border-color: #ce93d8;
}

/* Three side-by-side language buttons in the Send Packet dialog. Small
   gap between them; vuetify's default v-card-actions gap is too wide. */
.lang-option-btn + .lang-option-btn {
  margin-left: 4px;
}

/* Send Packet dialog — give text-field + select breathing room. */
.send-field {
  margin-bottom: 16px;
}
.send-field:last-child {
  margin-bottom: 0;
}

/* Status badge — shared styling. Uniform min-width across all three
   statuses so rows line up; no jitter when status changes between
   "New" / "In progress" / "Submitted". */
.status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 7px;
  border-radius: 3px;
  flex-shrink: 0;
  min-width: 72px;
  text-align: center;
  display: inline-block;
}
.status-badge.status-created {
  background-color: #f0f0f0;
  color: #555;
  border: 1px solid #d0d0d0;
}
.status-badge.status-in_progress {
  background-color: #fff4e0;
  color: #b87100;
  border: 1px solid #ffd599;
}
.status-badge.status-submitted {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.unacked-badge {
  background-color: #d32f2f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 18px;
  text-align: center;
  flex-shrink: 0;
}
.delivery-failed-badge {
  background-color: #ff9800;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* Per-row agent dropdown — sits inline in row-line-1, positioned by the
   2x/1x flex spacers around it. Compact: small font, short height. */
.row-agent-select {
  flex: 0 0 160px;
  max-width: 160px;
}
.row-agent-select :deep(.v-field) {
  border-radius: 3px;
  font-size: 12px;
  min-height: 28px;
}
.row-agent-select :deep(.v-field__input) {
  min-height: 26px;
  padding-top: 2px;
  padding-bottom: 2px;
  font-size: 12px;
}
.row-agent-select :deep(.v-field__field) {
  min-height: 26px;
}
.row-agent-select :deep(.v-field__append-inner) {
  padding-top: 0;
  align-self: center;
}

/* Kebab icon at the right end of row line 1. Subtle gray; hover
   tints to indicate clickability. @click.stop on the icon (in template)
   prevents the row from toggling when the menu is opened. */
.kebab-icon {
  color: #999 !important;
  cursor: pointer;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  margin-left: 4px;
}
.kebab-icon:hover {
  color: #555 !important;
  background-color: rgba(0, 0, 0, 0.05);
}

/* The popped menu list — keep it compact. */
.kebab-menu-list {
  min-width: 140px;
}

/* =================================================================
   Detail content (inside expanded panel)
   ================================================================= */
.loading-detail {
  display: flex;
  align-items: center;
  padding: 16px 0;
  color: #666;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 4px 24px;
  font-size: 13px;
  color: #555;
}
.detail-info-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.detail-info-row label {
  color: #999;
  flex-shrink: 0;
  min-width: 90px;
}

/* The Assigned row — slightly taller than the others because of the
   combobox. align-items: center on .detail-info-row keeps the label
   aligned with the field's vertical midline. */
.assigned-row {
  /* Span all grid columns so the combobox doesn't squeeze with siblings */
  grid-column: 1 / -1;
}

.agent-combobox {
  flex: 0 1 240px;
}

.agent-saving-text {
  font-size: 12px;
  color: #888;
  font-style: italic;
}

.detail-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.action-button {
  background-color: #d2b48c !important;
  flex-shrink: 0;
}

/* Thread */
.thread-section {
  background-color: #fff;
  border: 1px solid #e0d5c0;
  border-radius: 4px;
  padding: 12px 16px;
}

.thread-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #555;
}
.thread-count {
  color: #999;
  font-weight: 400;
  font-size: 13px;
}

.no-messages {
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 16px;
}

.thread-list {
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 4px;
}

/* Each message is a row card — header line + body, no chat-bubble
   alignment. Same convention as AABBSms message cards. */
.message-row {
  background-color: #faf8f0;
  border: 1px solid #e0d5c0;
  border-radius: 4px;
  padding: 8px 12px;
}

.message-row-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

/* Direction badge — same uniform-width treatment as the status badge
   so all messages line up cleanly. */
.direction-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 7px;
  border-radius: 3px;
  flex-shrink: 0;
  min-width: 62px;
  text-align: center;
  display: inline-block;
}
.direction-badge.inbound {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}
.direction-badge.outbound {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.message-timestamp {
  font-size: 12px;
  color: #888;
}

.message-attribution {
  font-size: 12px;
  color: #888;
  font-style: italic;
}

.message-row-body {
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
}

/* Image thumbnails attached to a message. Inline strip below the body. */
.message-thumbnails {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.thumbnail-link {
  display: block;
  line-height: 0;
}

.thumbnail-img {
  max-width: 120px;
  max-height: 120px;
  border-radius: 4px;
  border: 1px solid #e0d5c0;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.1s;
}

.thumbnail-img:hover {
  transform: scale(1.03);
}

/* Compose box — single-line text input next to Send button, both at the
   same height. align-items: center so they line up vertically. */
.compose-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0d5c0;
}
.compose-input {
  flex: 1;
}
.send-sms-button {
  background-color: #d2b48c !important;
  flex-shrink: 0;
}

/* =================================================================
   Outer accordion header / body — same as Step 11.
   The `>` direct-child operator below scopes these to the OUTER
   accordion only; the inner .packet-panel rules above handle the
   nested accordion styling.
   ================================================================= */
:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  background-color: #d2b48c !important;
  border: 1px solid #d2b48c !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
  color: #ffffff !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-text) {
  background-color: #f5f5dc !important;
  border: 1px solid #d2b48c !important;
  border-top: none !important;
}

#indemnitor-panel {
  margin-bottom: 8px;
}

@media (max-width: 600px) {
  .controls-row {
    flex-direction: column;
    align-items: stretch;
  }
  .controls-spacer { display: none; }
  .agent-field, .search-field { flex: 1; }

  .row-line-1, .row-line-2 { flex-wrap: wrap; }

  .detail-info-grid {
    grid-template-columns: 1fr;
  }
}

/* =================================================================
   Email PDF dialogs
   ================================================================= */
.dialog-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  padding: 16px 20px 8px 20px !important;
}

.dialog-body {
  padding: 8px 20px 16px 20px !important;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.dialog-body p {
  margin: 0 0 12px 0;
}

.dialog-body p:last-child {
  margin-bottom: 0;
}

.dialog-prompt {
  font-weight: 500;
  color: #555;
}

.empty-users-note {
  color: #888;
  font-style: italic;
}

/* PDF preview modal — large iframe, minimal chrome. */
.pdf-dialog-title {
  display: flex;
  align-items: center;
  font-size: 16px !important;
  font-weight: 600 !important;
  padding: 12px 16px !important;
}

.pdf-dialog-body {
  padding: 0 !important;
  height: 70vh;
  overflow: hidden;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.pdf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  height: 100%;
}
.pdf-loading .loading-text {
  margin-top: 12px;
  color: #666;
}
</style>
